import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { siteConfig } from '@/mocks/site';

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const isHome = location.pathname === '/';
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const linkClass = (active: boolean) =>
    [
      'text-sm font-medium transition-colors duration-200',
      active
        ? 'text-accent-500'
        : transparent
          ? 'text-background-50/90 hover:text-background-50'
          : 'text-foreground-700 hover:text-primary-700',
    ].join(' ');

  return (
    <header
      className={[
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        transparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur border-b border-background-200',
      ].join(' ')}
    >
      <nav className="flex items-center justify-between h-16 md:h-[72px] px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span
            className={[
              'flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-md font-heading font-black text-lg',
              transparent ? 'bg-background-50/15 text-background-50' : 'bg-primary-500 text-background-50',
            ].join(' ')}
          >
            姐
          </span>
          <span className="flex flex-col leading-none">
            <span
              className={[
                'font-heading font-bold text-lg md:text-xl tracking-wide',
                transparent ? 'text-background-50' : 'text-foreground-950',
              ].join(' ')}
            >
              {siteConfig.brand}
            </span>
            <span
              className={[
                'text-[10px] tracking-[0.25em] mt-0.5 hidden sm:block',
                transparent ? 'text-background-100/70' : 'text-foreground-400',
              ].join(' ')}
            >
              {siteConfig.brandEn}
            </span>
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-8">
          {siteConfig.nav.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} className={({ isActive }) => linkClass(isActive)}>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2 md:gap-3">
          <button
            type="button"
            aria-label="搜索"
            onClick={() => navigate('/search')}
            className={[
              'flex items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full transition-colors cursor-pointer',
              transparent
                ? 'text-background-50 hover:bg-background-50/15'
                : 'text-foreground-700 hover:bg-background-200',
            ].join(' ')}
          >
            <i className="ri-search-line text-lg" />
          </button>
          <button
            type="button"
            aria-label="菜单"
            onClick={() => setMobileOpen((v) => !v)}
            className={[
              'md:hidden flex items-center justify-center w-9 h-9 rounded-full transition-colors cursor-pointer',
              transparent ? 'text-background-50' : 'text-foreground-800',
            ].join(' ')}
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-b border-background-200 px-4 py-3">
          <ul className="flex flex-col">
            {siteConfig.nav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    [
                      'block py-3 text-base border-b border-background-100 transition-colors',
                      isActive ? 'text-accent-500 font-semibold' : 'text-foreground-700',
                    ].join(' ')
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </header>
  );
}